import {
  CompletionProgress,
  Phase as FlexiblePhase,
  PhaseContent as FlexiblePhaseContent,
  PhaseInstructionOverride,
  PhaseInstructions,
  PhasesView,
} from '@ui-components/layout/PhasesView'
import { Ref } from 'react'
import { useIntl } from 'react-intl'

import { usePageTitle } from '../hooks/usePageTitle'

export interface PhaseContent extends FlexiblePhaseContent {
  action: {
    kind: 'content'
    label?: string
    onClick: () => unknown
    dataTestId?: string
  }
}

type FinalizationContent = Exclude<FlexiblePhaseContent, 'action'>

export type Phase = Omit<FlexiblePhase, 'content'> &
  (
    | {
        header: string
        content: PhaseContent
        isComplete: boolean
      }
    | {
        header: string
        content: FinalizationContent
        isComplete: boolean
        isFinalizationPhase: true
      }
  )

export interface PhasesProps {
  phases: Phase[]
  isSummaryLikeView: boolean
  headerOverride?: string
  instructionOverrides?: PhaseInstructionOverride
  ref?: Ref<HTMLDivElement>
  testId?: string
  /** set true to externally fix completion status to complete instead of determining from phases completeness.
   * Does not change phase completion.
   */
  hasCompletedOverride?: boolean
}

export const FullPagePhases = (props: PhasesProps) => {
  return <PhasesBase {...props} fullViewportHeight={true} />
}

export const Phases = (props: PhasesProps) => {
  return <PhasesBase {...props} fullViewportHeight={false} />
}

const PhasesBase = (props: PhasesProps & { fullViewportHeight: boolean }) => {
  const intl = useIntl()
  const {
    phases,
    headerOverride,
    instructionOverrides,
    isSummaryLikeView,
    fullViewportHeight,
    ref,
    testId,
    hasCompletedOverride,
  } = props

  const phaseInstructionTexts: PhaseInstructions = {
    [CompletionProgress.NotStarted]: {
      title: intl.formatMessage({ id: 'common-phase-progress-not-started-header' }),
      text: intl.formatMessage({ id: 'common-phases-progress-continue-to-next-description' }),
      additionalText: intl.formatMessage({ id: 'common-required-fields-instruction-text' }),
      ...instructionOverrides?.initial,
    },
    [CompletionProgress.Started]: {
      title: intl.formatMessage({ id: 'common-phase-progress-started-header' }),
      text: intl.formatMessage({ id: 'common-phases-progress-continue-to-next-description' }),
      ...instructionOverrides?.started,
    },
    [CompletionProgress.Incomplete]: {
      title: intl.formatMessage({ id: 'common-phase-progress-in-progress-header' }),
      text: intl.formatMessage({ id: 'common-phases-progress-continue-to-next-description' }),
      ...instructionOverrides?.inProgress,
    },
    [CompletionProgress.IncompletePastHalf]: {
      title: intl.formatMessage({ id: 'common-phase-progress-halfway-header' }),
      text: intl.formatMessage({ id: 'common-phases-progress-continue-to-next-description' }),
      ...instructionOverrides?.pastHalfway,
    },
    [CompletionProgress.Complete]: {
      title: intl.formatMessage({ id: 'common-phase-progress-finalizing-header' }),
      text: intl.formatMessage({ id: 'common-progress-finalizing-description' }),
      ...instructionOverrides?.complete,
    },
  }

  const completePhaseCount = phases.filter((phase) => phase.isComplete).length
  const incompletePhaseCount = phases.length - completePhaseCount

  let progress: CompletionProgress

  const midPoint = Math.floor(phases.length / 2) + (phases.length % 2) // For odd counts halfway is one past the absolute middle
  if (completePhaseCount === 0) {
    progress = CompletionProgress.NotStarted
  } else if (phases.every((phase) => phase.isComplete) || incompletePhaseCount === 1) {
    progress = CompletionProgress.Complete
  } else if (completePhaseCount === 1) {
    progress = CompletionProgress.Started
  } else if (completePhaseCount === midPoint) {
    progress = CompletionProgress.IncompletePastHalf
  } else {
    progress = CompletionProgress.Incomplete
  }
  const guidance = phaseInstructionTexts[progress]

  usePageTitle(guidance.title)

  return (
    <PhasesView
      ref={ref}
      testId={testId}
      progress={hasCompletedOverride ? CompletionProgress.Complete : progress}
      instructions={phaseInstructionTexts}
      phasesHeader={
        headerOverride ??
        (isSummaryLikeView
          ? intl.formatMessage({ id: 'common-progress-phases-counter-left-header' })
          : intl.formatMessage({ id: 'common-progress-phases-no-counter-left-header' }))
      }
      phases={phases}
      shouldShowCompletedPhases={!isSummaryLikeView}
      fullViewportHeight={fullViewportHeight}
    />
  )
}
